// ═══════════════════════════════════════════════════════════════════
//  TOQUM · LANDING PAGE — AI-first home experience
//  - Claude-style welcome + chat
//  - Three shortcut tiles: Dashboard / Inspections / Manage
//  - Default page checkboxes (saved per user in localStorage)
//  - Today's inspection popup with geo-tag prompt
//  - All queries answered via Andon AI in-page
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { API_URL, T, today, num } from './Shared';

const ANDON_URL = `${API_URL}/andon.php`;

const MODULES_LABELS = {
  HOME:'Home', DASHBOARD:'Dashboard', QA:'Inspections',
  CD:'Commercial Decisions',
  SUPPLIER:'Supplier Management', STYLE:'Style & POs',
  SAMPLE:'Sample Evaluation', TRAINING:'Training',
  PROJECTS:'Projects', FEEDBACK:'Retail Voice',
  TEAM:'Team', ADMIN:'Admin',
};

// ── Markdown renderer (same as Andon) ─────────────────────────────
function MD({ text }) {
  const html = (text || '')
    .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
    .replace(/\*\*(.+?)\*\*/g,'<b>$1</b>')
    .replace(/\*(.+?)\*/g,'<i>$1</i>')
    .replace(/`(.+?)`/g,'<code style="background:#f1f5f9;padding:1px 5px;border-radius:4px;font-size:0.9em">$1</code>')
    .replace(/\n/g,'<br/>');
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function Dots() {
  return (
    <span style={{ display:'inline-flex', gap:3 }}>
      {[0,1,2].map(i => (
        <span key={i} style={{ width:5, height:5, borderRadius:'50%',
          background:'#8a94a6',
          animation:`landingBounce 1.2s ${i*0.15}s infinite ease-in-out` }}/>
      ))}
    </span>
  );
}

// ── Geo-tag helper ─────────────────────────────────────────────────
function useGeo() {
  const [geo, setGeo] = useState(null);
  const [geoErr, setGeoErr] = useState(null);
  const get = useCallback(() => {
    if (!navigator.geolocation) { setGeoErr('Geolocation not supported'); return; }
    navigator.geolocation.getCurrentPosition(
      p => setGeo({ lat: p.coords.latitude.toFixed(6), lng: p.coords.longitude.toFixed(6) }),
      e => setGeoErr(e.message),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  }, []);
  return { geo, geoErr, get };
}

// ── Inspection popup ───────────────────────────────────────────────
function InspectionPopup({ inspections, visitPlans, onGo, onDismiss }) {
  const { geo, geoErr, get } = useGeo();
  const [tagged, setTagged] = useState(false);

  const todayInsp = inspections.filter(i =>
    i.date === today() && i.status === 'Scheduled'
  );
  const todayVisits = visitPlans.filter(v =>
    v.date === today() && v.status !== 'Completed'
  );
  const all = [...todayInsp, ...todayVisits];
  if (!all.length) return null;

  return (
    <div style={{ position:'fixed', inset:0, zIndex:4000,
      background:'rgba(10,14,26,0.65)', display:'flex',
      alignItems:'center', justifyContent:'center',
      padding:20, backdropFilter:'blur(6px)' }}>
      <div style={{ background:'white', borderRadius:18, width:'100%', maxWidth:480,
        boxShadow:'0 32px 80px rgba(0,0,0,0.35)', overflow:'hidden',
        animation:'landingSlideUp .3s cubic-bezier(.34,1.56,.64,1)' }}>

        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#0a0e1a,#1a2236)',
          padding:'20px 22px', color:'white' }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.12em',
            textTransform:'uppercase', color:'rgba(255,255,255,0.5)', marginBottom:4 }}>
            Today's Schedule
          </div>
          <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",
            fontSize:20, fontWeight:800 }}>
            {all.length} Inspection{all.length>1?'s':''} Due Today
          </div>
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.6)', marginTop:4 }}>
            {today()} · Geo-tag your location before starting
          </div>
        </div>

        <div style={{ padding:'18px 22px' }}>
          {/* Inspections list */}
          <div style={{ marginBottom:16, display:'flex', flexDirection:'column', gap:8 }}>
            {all.map((item, i) => (
              <div key={i} style={{ background:'#f8f9fc', borderRadius:10,
                padding:'10px 14px', border:'1px solid #eef0f6' }}>
                <div style={{ fontWeight:700, fontSize:13.5, color:'#0a0e1a' }}>
                  {item.supplier || item.supplierName || 'Inspection'}
                </div>
                <div style={{ fontSize:11.5, color:'#8a94a6', marginTop:2 }}>
                  {item.poNo || item.poNo || '—'} ·{' '}
                  {item.stage || item.type || 'Inspection'} ·{' '}
                  {item.styleNo || item.styleNo || ''}
                </div>
              </div>
            ))}
          </div>

          {/* Geo-tag */}
          <div style={{ background: geo ? '#f0fdf4' : '#f8f9fc',
            border:`1px solid ${geo?'#86efac':'#eef0f6'}`,
            borderRadius:10, padding:'12px 14px', marginBottom:16 }}>
            <div style={{ fontSize:11.5, fontWeight:700, color:'#3d4660', marginBottom:8 }}>
              📍 Location Verification
            </div>
            {geo ? (
              <div style={{ fontSize:12, color:'#0a7d43', fontWeight:600 }}>
                ✓ Location captured — {geo.lat}, {geo.lng}
              </div>
            ) : geoErr ? (
              <div style={{ fontSize:12, color:'#b4232f' }}>⚠ {geoErr}</div>
            ) : (
              <div style={{ fontSize:11.5, color:'#8a94a6' }}>
                Location must be verified before starting inspection.
                This confirms you are physically at the factory.
              </div>
            )}
            {!geo && (
              <button onClick={() => { get(); setTagged(true); }}
                style={{ marginTop:8, padding:'7px 16px',
                  background:'linear-gradient(135deg,#4f6ef7,#3a56d4)',
                  color:'white', border:'none', borderRadius:7,
                  fontSize:12, fontWeight:700, cursor:'pointer',
                  boxShadow:'0 2px 8px rgba(79,110,247,0.3)' }}>
                📍 Tag My Location
              </button>
            )}
          </div>

          {/* Buttons */}
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => onGo(all[0], geo)}
              disabled={!geo}
              style={{ flex:1, padding:'12px',
                background: geo
                  ? 'linear-gradient(135deg,#06d6a0,#0ab885)'
                  : '#d0d4e4',
                color:'white', border:'none', borderRadius:9,
                fontSize:13, fontWeight:800, cursor: geo?'pointer':'not-allowed',
                boxShadow: geo?'0 3px 12px rgba(6,214,160,0.4)':'none' }}>
              {geo ? '✓ Start Inspection' : 'Tag Location First'}
            </button>
            <button onClick={onDismiss}
              style={{ padding:'12px 18px', background:'white',
                border:'1.5px solid #eef0f6', borderRadius:9,
                fontSize:12, fontWeight:700, color:'#8a94a6', cursor:'pointer' }}>
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Suggestion chips ───────────────────────────────────────────────
// Static - defined outside component to avoid recreation on every render
const SUGGESTIONS = [
  'Quality performance this month',
  'Which suppliers have the most defects?',
  'Show me open CAPs and exceptions',
  'Inspections scheduled this week',
  'DHU trend for last 3 months',
  'Top defect types across all factories',
];

// ── Main Landing Page ──────────────────────────────────────────────
export default function LandingPage({ db, currentUser, setActiveModule, saveToDB }) {
  const [msgs, setMsgs]         = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPopup, setShowPopup] = useState(true);
  const [prefs, setPrefs]       = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`tq_prefs_${currentUser?.username}`) || '{}');
    } catch { return {}; }
  });
  const chatEnd = useRef();
  const inputRef = useRef();

  const hour   = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const name   = (currentUser?.name || '').split(' ')[0];

  // Save prefs to localStorage
  const savePref = (key, val) => {
    const updated = { ...prefs, [key]: val };
    setPrefs(updated);
    localStorage.setItem(`tq_prefs_${currentUser?.username}`, JSON.stringify(updated));
  };

  const [redirectCountdown, setRedirectCountdown] = useState(null);

  // If user set a default page, show countdown banner instead of instant redirect
  useEffect(() => {
    if (!prefs.defaultPage || prefs.defaultPage === 'HOME') return;
    let count = 3;
    setRedirectCountdown(count);
    const interval = setInterval(() => {
      count--;
      if (count <= 0) {
        clearInterval(interval);
        setRedirectCountdown(null);
        setActiveModule(prefs.defaultPage);
      } else {
        setRedirectCountdown(count);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  // Scroll to bottom
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior:'smooth' }); }, [msgs, loading]);

  // Today's inspections
  const todayInsp = (db.inspections||[]).filter(i =>
    i.date === today() && i.status === 'Scheduled' &&
    (i.qaMember === currentUser?.name || true) // QA sees their own
  );
  const todayVisits = (db.visitPlans||[]).filter(v => v.date === today());

  // Send message
  // Navigation intent detection — "open X", "go to X", "show me X"
  const NAV_MAP = {
    'DASHBOARD':['dashboard','home page','main page','kpi','overview'],
    'QA':       ['inspection','qa','quality assurance','check','audit inspection'],
    'CD':       ['commercial','cd','commercial decision','exemption','approved orders','exception approvals'],
    'SUPPLIER': ['supplier','vendor','factory','manage','management'],
    'STYLE':    ['style','styles','techpack','tech pack','bom','spec sheet'],
    'SAMPLE':   ['sample','sample eval','sample evaluation'],
    'TRAINING': ['training','course','learn','learning','cqb','certificate'],
    'PROJECTS': ['project','dmaic','improvement','six sigma'],
    'FEEDBACK': ['feedback','complaint','retail','customer feedback','retail voice'],
    'TEAM':     ['team','staff','member','directory'],
    'ADMIN':    ['admin','administration','settings','user management'],
  };
  const NAV_TRIGGERS = ['open','go to','show','take me to','navigate to','launch','switch to','load'];

  const checkNavIntent = (text) => {
    const t = text.toLowerCase().trim();
    const hasNav = NAV_TRIGGERS.some(trigger => t.startsWith(trigger) || t.includes(trigger+' '));
    if (!hasNav) return null;
    for (const [module, keywords] of Object.entries(NAV_MAP)) {
      if (keywords.some(k => t.includes(k))) return module;
    }
    return null;
  };

  const send = useCallback(async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setInput('');

    // Check for navigation intent first
    const navTarget = checkNavIntent(q);
    if (navTarget) {
      setMsgs(p => [...p,
        { role:'user', content:q, ts:Date.now() },
        { role:'assistant', ts:Date.now(),
          content:`Opening **${MODULES_LABELS[navTarget] || navTarget}** for you…` }
      ]);
      setTimeout(() => setActiveModule(navTarget), 600);
      return;
    }
    const newMsgs = [...msgs, { role:'user', content:q, ts:Date.now() }];
    setMsgs(newMsgs);
    setLoading(true);

    try {
      const res = await fetch(ANDON_URL, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          role: currentUser?.role?.toLowerCase()||'qa',
          user_name: currentUser?.name,
          messages: newMsgs.map(m=>({ role:m.role, content:m.content })),
          context_summary: `Suppliers:${(db.suppliers||[]).length} POs:${(db.pos||[]).length} Inspections:${(db.inspections||[]).length}`,
        })
      }).then(r => r.json());

      const reply = res.reply || res.content?.[0]?.text
        || 'I can answer questions about your Toqum data — inspections, suppliers, defects, CAPs and more.';
      setMsgs(p => [...p, { role:'assistant', content:reply, ts:Date.now() }]);
    } catch {
      setMsgs(p => [...p, { role:'assistant', ts:Date.now(),
        content:'Could not reach the AI service. Check your API key in `api/andon.php`.' }]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [input, loading, msgs, currentUser, db]);

  const SHORTCUTS = [
    { key:'DASHBOARD', label:'Dashboard',          icon:'📊', sub:'KPIs, charts, trends' },
    { key:'QA',        label:'Inspections',        icon:'✅', sub:'Start or view inspections' },
    { key:'SUPPLIER',  label:'Manage',             icon:'⚙️', sub:'Suppliers, POs, styles' },
    { key:'CD',        label:'Commercial Decisions', icon:'🔴', sub:'Exemptions & CD approvals' },
  ];

  return (
    <>
      <style>{`
        @keyframes landingBounce{0%,80%,100%{transform:translateY(0)}40%{transform:translateY(-6px)}}
        @keyframes landingSlideUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
        @keyframes landingFadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        .landing-msg{animation:landingFadeIn .25s ease-out}
        .landing-chip:hover{background:#eef2ff!important;border-color:#4f6ef7!important;color:#3a56d4!important}
        .landing-shortcut:hover{box-shadow:0 4px 20px rgba(0,0,0,0.1)!important;transform:translateY(-2px)!important}
        .landing-input:focus{border-color:#4f6ef7!important;box-shadow:0 0 0 3px rgba(79,110,247,0.12)!important}
        .landing-send:hover{background:linear-gradient(135deg,#3a56d4,#2a42c0)!important}
        .landing-send:disabled{background:#d0d4e4!important}
      `}</style>

      {/* Inspection popup */}
      {showPopup && (todayInsp.length > 0 || todayVisits.length > 0) && (
        <InspectionPopup
          inspections={todayInsp}
          visitPlans={todayVisits}
          onGo={(item, geo) => {
            setShowPopup(false);
            setActiveModule('QA');
          }}
          onDismiss={() => setShowPopup(false)}
        />
      )}

      <div style={{ display:'flex', flexDirection:'column', height:'100%',
        background:'linear-gradient(180deg,#f8f9ff 0%,#f0f2f7 100%)',
        fontFamily:"'Inter',sans-serif" }}>

        {/* ── Main content area ── */}
        <div style={{ flex:1, overflowY:'auto', display:'flex',
          flexDirection:'column', alignItems:'center',
          padding:'40px 20px 20px' }}>

          {msgs.length === 0 && (
            <div style={{ width:'100%', maxWidth:700,
              animation:'landingFadeIn .4s ease-out' }}>

              {/* Welcome */}
              <div style={{ textAlign:'center', marginBottom:44 }}>
                <div style={{ width:56, height:56, borderRadius:16,
                  background:'linear-gradient(135deg,#4f6ef7,#06d6a0)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                  fontWeight:900, fontSize:22, color:'white',
                  margin:'0 auto 18px', letterSpacing:'-0.5px',
                  boxShadow:'0 4px 20px rgba(79,110,247,0.35)' }}>
                  TQ
                </div>
                <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",
                  fontSize:28, fontWeight:800, color:'#0a0e1a',
                  marginBottom:8, letterSpacing:'-0.02em' }}>
                  {greeting}, {name}
                </div>
                <div style={{ fontSize:15, color:'#8a94a6', fontWeight:400 }}>
                  What would you like to do today?
                </div>
              </div>

              {/* Three shortcut tiles */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)',
                gap:14, marginBottom:36 }}>
                {SHORTCUTS.map(s => (
                  <div key={s.key}
                    className="landing-shortcut"
                    style={{ background:'white', borderRadius:14,
                      border:'1.5px solid #eef0f6', padding:'18px 16px',
                      cursor:'pointer', transition:'all .2s',
                      boxShadow:'0 1px 4px rgba(0,0,0,0.05)',
                      position:'relative' }}>
                    {/* Default checkbox */}
                    <div style={{ position:'absolute', top:10, right:10,
                      display:'flex', alignItems:'center', gap:4 }}
                      onClick={e => {
                        e.stopPropagation();
                        savePref('defaultPage', prefs.defaultPage === s.key ? 'HOME' : s.key);
                      }}>
                      <input type="checkbox"
                        checked={prefs.defaultPage === s.key}
                        onChange={() => {}}
                        style={{ cursor:'pointer', accentColor:'#4f6ef7',
                          width:13, height:13 }}/>
                      <span style={{ fontSize:9, color:'#a8b0c0',
                        fontWeight:600, letterSpacing:'0.06em',
                        textTransform:'uppercase' }}>Default</span>
                    </div>

                    <div onClick={() => setActiveModule(s.key)}>
                      <div style={{ fontSize:26, marginBottom:10 }}>{s.icon}</div>
                      <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif",
                        fontWeight:700, fontSize:14, color:'#0a0e1a',
                        marginBottom:3 }}>{s.label}</div>
                      <div style={{ fontSize:11.5, color:'#8a94a6' }}>{s.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Today's summary strip */}
              {(todayInsp.length > 0 || todayVisits.length > 0) && (
                <div style={{ background:'linear-gradient(135deg,#fff8e6,#fff3d6)',
                  border:'1.5px solid #fde68a', borderRadius:12,
                  padding:'12px 16px', marginBottom:28,
                  display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                  <span style={{ fontSize:20 }}>📋</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:'#7c5c00' }}>
                      {todayInsp.length + todayVisits.length} inspection{(todayInsp.length+todayVisits.length)>1?'s':''} scheduled for today
                    </div>
                    <div style={{ fontSize:11.5, color:'#a07830', marginTop:1 }}>
                      Tap to start — geo-tag required before beginning
                    </div>
                  </div>
                  <button onClick={() => setShowPopup(true)}
                    style={{ padding:'7px 16px',
                      background:'linear-gradient(135deg,#ffd166,#f4a261)',
                      border:'none', borderRadius:8, fontWeight:700,
                      fontSize:12, color:'white', cursor:'pointer',
                      boxShadow:'0 2px 8px rgba(244,162,97,0.4)' }}>
                    View →
                  </button>
                </div>
              )}

              {/* Suggestion chips */}
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:11.5, color:'#8a94a6', fontWeight:600,
                  textTransform:'uppercase', letterSpacing:'0.08em',
                  marginBottom:10, textAlign:'center' }}>
                  Ask Andon anything
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8,
                  justifyContent:'center' }}>
                  {SUGGESTIONS.map(s => (
                    <button key={s} className="landing-chip"
                      onClick={() => send(s)}
                      style={{ padding:'7px 14px', border:'1.5px solid #eef0f6',
                        borderRadius:20, background:'white', fontSize:12,
                        color:'#5a6278', cursor:'pointer',
                        transition:'all .15s', fontFamily:"'Inter',sans-serif",
                        fontWeight:500 }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Chat messages */}
          {msgs.length > 0 && (
            <div style={{ width:'100%', maxWidth:700,
              display:'flex', flexDirection:'column', gap:16 }}>
              {msgs.map((m, i) => (
                <div key={i} className="landing-msg"
                  style={{ display:'flex',
                    justifyContent: m.role==='user' ? 'flex-end' : 'flex-start',
                    alignItems:'flex-start', gap:10 }}>
                  {m.role === 'assistant' && (
                    <div style={{ width:30, height:30, borderRadius:9, flexShrink:0,
                      background:'linear-gradient(135deg,#4f6ef7,#06d6a0)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontFamily:"'Plus Jakarta Sans',sans-serif",
                      fontWeight:900, fontSize:11, color:'white', marginTop:2 }}>
                      TQ
                    </div>
                  )}
                  <div style={{ maxWidth:'80%',
                    background: m.role==='user'
                      ? 'linear-gradient(135deg,#4f6ef7,#3a56d4)' : 'white',
                    color: m.role==='user' ? 'white' : '#1a1d2e',
                    padding:'12px 16px', borderRadius:14,
                    borderBottomRightRadius: m.role==='user' ? 4 : 14,
                    borderBottomLeftRadius:  m.role==='user' ? 14 : 4,
                    fontSize:13.5, lineHeight:1.65,
                    boxShadow: m.role==='user'
                      ? '0 2px 12px rgba(79,110,247,0.3)'
                      : '0 1px 4px rgba(0,0,0,0.06)',
                    border: m.role==='user' ? 'none' : '1px solid #eef0f6' }}>
                    <MD text={m.content}/>
                    <div style={{ fontSize:10, opacity:0.5, marginTop:5,
                      textAlign: m.role==='user' ? 'right' : 'left' }}>
                      {new Date(m.ts).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}
                    </div>
                  </div>
                </div>
              ))}
              {loading && (
                <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}
                  className="landing-msg">
                  <div style={{ width:30, height:30, borderRadius:9, flexShrink:0,
                    background:'linear-gradient(135deg,#4f6ef7,#06d6a0)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontFamily:"'Plus Jakarta Sans',sans-serif",
                    fontWeight:900, fontSize:11, color:'white' }}>
                    TQ
                  </div>
                  <div style={{ background:'white', padding:'12px 16px',
                    borderRadius:14, borderBottomLeftRadius:4,
                    border:'1px solid #eef0f6',
                    boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                    <Dots/>
                  </div>
                </div>
              )}
              <div ref={chatEnd}/>

              {/* Quick nav after chat */}
              <div style={{ display:'flex', gap:8, justifyContent:'center',
                flexWrap:'wrap', marginTop:8 }}>
                {SHORTCUTS.map(s => (
                  <button key={s.key} onClick={() => setActiveModule(s.key)}
                    style={{ padding:'6px 14px', border:'1.5px solid #eef0f6',
                      borderRadius:20, background:'white', fontSize:11.5,
                      color:'#5a6278', cursor:'pointer', fontFamily:"'Inter',sans-serif",
                      fontWeight:600 }}>
                    {s.icon} {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Redirect countdown banner */}
        {redirectCountdown !== null && (
          <div style={{ padding:'10px 20px', background:'#eef2ff',
            borderTop:'1px solid #c7d2f8', display:'flex',
            alignItems:'center', justifyContent:'center', gap:12 }}>
            <span style={{ fontSize:12.5, color:'#3a56d4', fontWeight:600 }}>
              Opening {MODULES_LABELS[prefs.defaultPage]} in {redirectCountdown}s…
            </span>
            <button onClick={()=>{ setRedirectCountdown(null); savePref('defaultPage','HOME'); }}
              style={{ padding:'4px 12px', background:'white',
                border:'1.5px solid #c7d2f8', borderRadius:6,
                fontSize:11.5, fontWeight:700, color:'#3a56d4', cursor:'pointer' }}>
              Stay Here
            </button>
          </div>
        )}

        {/* ── Chat input bar ── */}
        <div style={{ padding:'14px 20px 20px',
          borderTop:'1px solid #eef0f6', background:'white',
          boxShadow:'0 -4px 20px rgba(0,0,0,0.04)' }}>
          <div style={{ maxWidth:700, margin:'0 auto',
            display:'flex', gap:10, alignItems:'flex-end' }}>
            <textarea ref={inputRef}
              className="landing-input"
              value={input} rows={1}
              onChange={e => {
                setInput(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={e => {
                if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              placeholder="Ask about inspections, suppliers, defects, CAPs… or anything"
              style={{ flex:1, resize:'none', border:'1.5px solid #eef0f6',
                borderRadius:12, padding:'11px 14px',
                fontSize:14, outline:'none', lineHeight:1.5,
                fontFamily:"'Inter',sans-serif", color:'#1a1d2e',
                background:'#fafbfc', minHeight:44, overflow:'hidden',
                transition:'border-color .15s, box-shadow .15s' }}/>
            <button className="landing-send"
              onClick={() => send()}
              disabled={!input.trim() || loading}
              style={{ width:44, height:44, borderRadius:12, border:'none',
                background:'linear-gradient(135deg,#4f6ef7,#3a56d4)',
                color:'white', cursor:'pointer', fontSize:18,
                display:'flex', alignItems:'center', justifyContent:'center',
                flexShrink:0, transition:'all .15s',
                boxShadow:'0 2px 10px rgba(79,110,247,0.35)' }}>
              ➤
            </button>
          </div>
          <div style={{ maxWidth:700, margin:'6px auto 0',
            fontSize:10.5, color:'#a8b0c0', textAlign:'center' }}>
            Press Enter to send · Shift+Enter for new line ·
            Answers from your live Toqum data
          </div>
        </div>
      </div>
    </>
  );
}
